import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowRight, Check, Loader2, Mail, RotateCcw, Shield, Sparkles } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx'
import { Input } from '@/components/ui/input.tsx'

type CheckoutStatus = 'pending_payment' | 'pending_registration' | 'completed' | 'completed_existing' | 'expired' | 'failed_duplicate'

interface StatusData {
  status: CheckoutStatus
  email?: string
  first_name?: string
  subscription_category?: string
  is_expired?: boolean
}

export function WebinarCompletePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [statusData, setStatusData] = useState<StatusData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [gender, setGender] = useState<'male' | 'female' | undefined>(undefined)
  const [age, setAge] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [completedEmail, setCompletedEmail] = useState<string | null>(null)

  const sessionId = searchParams.get('session_id')
  const token = searchParams.get('token')

  useEffect(() => {
    fetchStatus()
  }, [sessionId, token])

  // Poll for status when pending_payment
  useEffect(() => {
    if (statusData?.status !== 'pending_payment') return

    const interval = setInterval(() => {
      fetchStatus()
    }, 3000)

    return () => clearInterval(interval)
  }, [statusData?.status])

  const fetchStatus = async () => {
    if (!sessionId && !token) {
      setError(t('webinar.complete.missingParams'))
      setIsLoading(false)
      return
    }

    try {
      const params = new URLSearchParams()
      if (sessionId) params.set('session_id', sessionId)
      if (token) params.set('token', token)

      const res = await fetch(`/api/webinar/status?${params.toString()}`, {
        credentials: 'include',
      })
      const data = await res.json()

      if (!res.ok || data.error) {
        throw new Error(data.error?.message || t('webinar.complete.statusError'))
      }

      setStatusData(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webinar.complete.statusError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password.length < 6) {
      setError(t('validation.passwordTooShort', { min: 6 }))
      return
    }

    if (password !== confirmPassword) {
      setError(t('webinar.complete.passwordMismatch'))
      return
    }

    if (!gender) {
      setError(t('validation.genderRequired'))
      return
    }

    if (!age) {
      setError(t('validation.ageRequired'))
      return
    }

    const ageNum = parseInt(age)
    if (isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
      setError(t('validation.ageInvalid'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const body: Record<string, string> = { password }
      if (sessionId) body.session_id = sessionId
      if (token) body.completion_token = token
      if (gender) body.gender = gender
      if (age) body.birth_year = String(new Date().getFullYear() - parseInt(age))

      const res = await fetch('/api/webinar/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await res.json()

      if (!res.ok || data.error) {
        throw new Error(data.error?.message || t('webinar.complete.completeError'))
      }

      // Show success screen with app download links
      setCompletedEmail(statusData?.email || data.email || '')
      setStatusData({ ...statusData!, status: 'completed' })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('webinar.complete.completeError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8E4F0] to-[#D6E5F0]">
        <div className="flex items-center gap-2 text-[#6D6D6D]">
          <Loader2 className="w-5 h-5 animate-spin" />
          {t('common.loading')}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E8E4F0] to-[#D6E5F0] p-4 sm:p-8">
      <div className="w-full max-w-md">
        {/* Pending Payment */}
        {statusData?.status === 'pending_payment' && (
          <Card>
            <CardHeader className="text-center">
              <Loader2 className="w-12 h-12 text-[#26247B] mx-auto mb-2 animate-spin" />
              <CardTitle>{t('webinar.complete.processingTitle')}</CardTitle>
              <CardDescription>{t('webinar.complete.processingDescription')}</CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Pending Registration */}
        {statusData?.status === 'pending_registration' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('webinar.complete.registerTitle')}</CardTitle>
              <CardDescription>{t('webinar.complete.registerDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Info fields */}
              <div className="space-y-3 mb-6">
                {statusData.email && (
                  <div className="rounded-lg bg-[#F4F3F9] px-4 py-3">
                    <p className="text-xs text-[#6D6D6D] mb-0.5">{t('common.email')}</p>
                    <p className="text-sm font-medium text-[#363636]">{statusData.email}</p>
                  </div>
                )}
                {statusData.first_name && (
                  <div className="rounded-lg bg-[#F4F3F9] px-4 py-3">
                    <p className="text-xs text-[#6D6D6D] mb-0.5">{t('common.firstName')}</p>
                    <p className="text-sm font-medium text-[#363636]">{statusData.first_name}</p>
                  </div>
                )}
                {statusData.subscription_category && (
                  <div className="rounded-lg bg-[#F4F3F9] px-4 py-3">
                    <p className="text-xs text-[#6D6D6D] mb-0.5">{t('webinar.complete.plan')}</p>
                    <p className="text-sm font-medium text-[#363636] capitalize">
                      {statusData.subscription_category}
                    </p>
                  </div>
                )}
              </div>

              {/* Password form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-[#363636] mb-1">
                    {t('common.password')}
                  </label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('webinar.complete.passwordPlaceholder')}
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-[#6D6D6D] mt-1">
                    {t('registration.passwordHelp')}
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#363636] mb-1">
                    {t('registration.confirmPassword')}
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('registration.confirmPasswordPlaceholder')}
                    required
                    minLength={6}
                  />
                </div>

                {/* Gender & birth fields */}
                <div>
                  <label className="block text-sm font-medium text-[#363636] mb-1">
                    {t('common.gender')}
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={gender === 'male'}
                        onChange={(e) => setGender(e.target.value as 'male' | 'female')}
                        disabled={isSubmitting}
                        className="w-4 h-4 cursor-pointer accent-[#26247B]"
                      />
                      <span className="text-sm text-[#363636] group-hover:text-[#26247B] transition-colors">{t('common.male')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={gender === 'female'}
                        onChange={(e) => setGender(e.target.value as 'male' | 'female')}
                        disabled={isSubmitting}
                        className="w-4 h-4 cursor-pointer accent-[#DA1D52]"
                      />
                      <span className="text-sm text-[#363636] group-hover:text-[#DA1D52] transition-colors">{t('common.female')}</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="age" className="block text-sm font-medium text-[#363636] mb-1">
                    {t('common.age')}
                  </label>
                  <Input
                    id="age"
                    type="number"
                    placeholder={t('webinar.complete.agePlaceholder')}
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    disabled={isSubmitting}
                    min={1}
                    max={120}
                    required
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full py-4 bg-[#26247B] hover:bg-[#1a1860] text-white font-medium rounded-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.loading')}
                    </span>
                  ) : (
                    t('webinar.complete.createAccount')
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Completed — New User App Download Screen */}
        {statusData?.status === 'completed' && (
          <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_8px_40px_rgba(21,9,88,0.12)]">
            {/* Top accent bar - hidden */}

            <div className="px-6 pt-8 pb-8 sm:px-8">
              {/* Success icon */}
              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center shadow-[0_0_0_6px_rgba(16,185,129,0.08)]">
                    <Check className="w-8 h-8 text-emerald-500" strokeWidth={2.5} />
                  </div>
                  <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-[#e8dbb5]" />
                </div>
              </div>

              <h2 className="text-xl font-bold text-[#26247B] text-center mb-1.5">
                {t('webinar.complete.successTitle')}
              </h2>
              <p className="text-sm text-[#757372] text-center mb-6 leading-relaxed">
                {t('webinar.complete.successDescription')}
              </p>

              {/* Account info */}
              {(completedEmail || statusData.email) && (
                <div className="flex items-center gap-3 rounded-xl border border-[#E8E4F0] bg-[#FAFAFE] px-4 py-3.5 mb-6">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#26247B]/5 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#26247B]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-[#757372] font-medium">
                      {t('webinar.complete.loginWith')}
                    </p>
                    <p className="text-sm font-semibold text-[#1a1615] truncate">
                      {completedEmail || statusData.email}
                    </p>
                  </div>
                </div>
              )}

              {/* App download section */}
              <div className="pt-1">
                <p className="text-[13px] text-[#757372] text-center mb-4 font-medium">
                  {t('webinar.complete.downloadPrompt')}
                </p>
                <div className="flex gap-3">
                  <a
                    href="https://apps.apple.com/app/german-method/id6504508910"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <button className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#E8E4F0] hover:bg-[#FAFAFE] px-4 py-3 transition-colors">
                      <svg className="w-[18px] h-[18px] text-[#26247B]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                      <span className="text-sm font-medium text-[#26247B]">App Store</span>
                    </button>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.deenakademie.germanmethod"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <button className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#E8E4F0] hover:bg-[#FAFAFE] px-4 py-3 transition-colors">
                      <svg className="w-[18px] h-[18px] text-[#26247B]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.18 23.72c-.36-.17-.6-.52-.6-.92V1.2c0-.4.24-.75.6-.92L14.56 12 3.18 23.72zm1.4.56L16.25 13.4l2.67 2.67L5.8 24.28c-.43.24-.89.23-1.22 0zM16.25 10.6L4.58-.28c.33-.23.79-.24 1.22 0l13.12 8.21-2.67 2.67zm1.27 1.27l3.37-3.37c.6.34.98.95.98 1.5s-.38 1.16-.98 1.5l-3.37-3.37v3.74z" />
                      </svg>
                      <span className="text-sm font-medium text-[#26247B]">Google Play</span>
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Completed Existing User */}
        {statusData?.status === 'completed_existing' && (
          <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_8px_40px_rgba(21,9,88,0.12)]">
            {/* Top accent bar - hidden */}

            <div className="px-6 pt-8 pb-8 sm:px-8">
              {/* Success icon */}
              <div className="flex justify-center mb-5">
                <div className="relative">
                  <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center shadow-[0_0_0_6px_rgba(16,185,129,0.08)]">
                    <Check className="w-8 h-8 text-emerald-500" strokeWidth={2.5} />
                  </div>
                  <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-[#e8dbb5]" />
                </div>
              </div>

              <h2 className="text-xl font-bold text-[#26247B] text-center mb-1.5">
                Abo aktiviert!
              </h2>
              <p className="text-sm text-[#757372] text-center mb-6 leading-relaxed">
                Dein Abo wurde erfolgreich aktiviert.<br />
                Melde dich mit deinem bestehenden Konto an.
              </p>

              {/* Account & plan info */}
              <div className="space-y-2.5 mb-6">
                {statusData.email && (
                  <div className="flex items-center gap-3 rounded-xl border border-[#E8E4F0] bg-[#FAFAFE] px-4 py-3.5">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#26247B]/5 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-[#26247B]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wider text-[#757372] font-medium">E-Mail</p>
                      <p className="text-sm font-semibold text-[#1a1615] truncate">{statusData.email}</p>
                    </div>
                  </div>
                )}
                {statusData.subscription_category && (
                  <div className="flex items-center gap-3 rounded-xl border border-[#E8E4F0] bg-[#FAFAFE] px-4 py-3.5">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#26247B]/5 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-[#26247B]" />
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-[#757372] font-medium">Abo</p>
                      <p className="text-sm font-semibold text-[#1a1615] capitalize">{statusData.subscription_category}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Login CTA */}
              <a href="/auth" className="block mb-5">
                <button className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#26247B] hover:bg-[#1a1860] text-white font-semibold py-3.5 px-6 transition-colors shadow-[0_4px_12px_rgba(21,9,88,0.3)]">
                  Zum Login
                  <ArrowRight className="w-4 h-4" />
                </button>
              </a>

              {/* App download section */}
              <div className="pt-1">
                <p className="text-[13px] text-[#757372] text-center mb-4 font-medium">
                  {t('webinar.complete.downloadPrompt')}
                </p>
                <div className="flex gap-3">
                  <a
                    href="https://apps.apple.com/app/german-method/id6504508910"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <button className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#E8E4F0] hover:bg-[#FAFAFE] px-4 py-3 transition-colors">
                      <svg className="w-[18px] h-[18px] text-[#26247B]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                      <span className="text-sm font-medium text-[#26247B]">App Store</span>
                    </button>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.deenakademie.germanmethod"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <button className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#E8E4F0] hover:bg-[#FAFAFE] px-4 py-3 transition-colors">
                      <svg className="w-[18px] h-[18px] text-[#26247B]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.18 23.72c-.36-.17-.6-.52-.6-.92V1.2c0-.4.24-.75.6-.92L14.56 12 3.18 23.72zm1.4.56L16.25 13.4l2.67 2.67L5.8 24.28c-.43.24-.89.23-1.22 0zM16.25 10.6L4.58-.28c.33-.23.79-.24 1.22 0l13.12 8.21-2.67 2.67zm1.27 1.27l3.37-3.37c.6.34.98.95.98 1.5s-.38 1.16-.98 1.5l-3.37-3.37v3.74z" />
                      </svg>
                      <span className="text-sm font-medium text-[#26247B]">Google Play</span>
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expired */}
        {statusData?.status === 'expired' && (
          <Card>
            <CardHeader className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
              <CardTitle>{t('webinar.complete.expiredTitle')}</CardTitle>
              <CardDescription>{t('webinar.complete.expiredDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <a href="/webinar/recover">
                <Button className="bg-[#26247B] hover:bg-[#1a1860] text-white">
                  {t('webinar.complete.recoverLink')}
                </Button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* Failed Duplicate — Already has active subscription */}
        {statusData?.status === 'failed_duplicate' && (
          <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_8px_40px_rgba(21,9,88,0.12)]">
            {/* Top accent bar - hidden */}

            <div className="px-6 pt-8 pb-8 sm:px-8">
              {/* Info icon */}
              <div className="flex justify-center mb-5">
                <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center shadow-[0_0_0_6px_rgba(245,158,11,0.08)]">
                  <RotateCcw className="w-7 h-7 text-amber-600" strokeWidth={2} />
                </div>
              </div>

              <h2 className="text-xl font-bold text-[#26247B] text-center mb-1.5">
                Bereits ein aktives Abo
              </h2>
              <p className="text-sm text-[#757372] text-center mb-6 leading-relaxed">
                Du hast bereits ein aktives Abo.<br />
                Die Zahlung wurde automatisch erstattet.
              </p>

              {/* Refund notice */}
              <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3.5 mb-6">
                <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-800 leading-relaxed">
                  Keine Sorge — die Erstattung wird innerhalb von 5-10 Werktagen auf deinem Konto erscheinen.
                </p>
              </div>

              {/* Login CTA */}
              <a href="/auth" className="block mb-5">
                <button className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#26247B] hover:bg-[#1a1860] text-white font-semibold py-3.5 px-6 transition-colors shadow-[0_4px_12px_rgba(21,9,88,0.3)]">
                  Zum bestehenden Konto
                  <ArrowRight className="w-4 h-4" />
                </button>
              </a>

              {/* App download — compact */}
              <div className="flex gap-3">
                <a
                  href="https://apps.apple.com/app/german-method/id6504508910"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <button className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#E8E4F0] hover:bg-[#FAFAFE] px-4 py-2.5 transition-colors">
                    <svg className="w-4 h-4 text-[#26247B]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    <span className="text-sm font-medium text-[#26247B]">App Store</span>
                  </button>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.deenakademie.germanmethod"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <button className="w-full flex items-center justify-center gap-2 rounded-lg border border-[#E8E4F0] hover:bg-[#FAFAFE] px-4 py-2.5 transition-colors">
                    <svg className="w-4 h-4 text-[#26247B]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.18 23.72c-.36-.17-.6-.52-.6-.92V1.2c0-.4.24-.75.6-.92L14.56 12 3.18 23.72zm1.4.56L16.25 13.4l2.67 2.67L5.8 24.28c-.43.24-.89.23-1.22 0zM16.25 10.6L4.58-.28c.33-.23.79-.24 1.22 0l13.12 8.21-2.67 2.67zm1.27 1.27l3.37-3.37c.6.34.98.95.98 1.5s-.38 1.16-.98 1.5l-3.37-3.37v3.74z" />
                    </svg>
                    <span className="text-sm font-medium text-[#26247B]">Google Play</span>
                  </button>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* General error when no status */}
        {!statusData && error && (
          <Card>
            <CardHeader className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
              <CardTitle>{t('webinar.complete.errorTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
